import React from 'react';
import styled from 'styled-components';
import { getJobWithCompany } from '../../../helpers/format';
import CardIcon from '../../../components/shared/card/CardIcon';
import InfoLine from '../../../components/shared/InfoLine';
import SocialLinks from '../../../components/shared/SocialLinks';
import IconPoi from '../../../components/icons/IconPoi';
import IconWebsite from '../../../components/icons/IconWebsite';
import { DownloadVCard, FavItem } from '../../../components/shared/ListItemDetails';
import Star from '../../../components/shared/fav/Star';
import Button from '../../../components/shared/Button';

const UserContainer = styled.div`
  cursor: pointer;
  text-align: center;
`;

const UserName = styled.div`
  font-size: 1.8rem;
  font-weight: 500;
`;

const UserJob = styled.div`
  padding: 5px 0;
  font-size: 1.4rem;
  color: ${props => props.theme.ashesGrey};
`;

const NotesSection = styled.div`
  margin-top: 30px;
  text-align: left;
`;

const NotesTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${props => props.theme.darkGrey};
  margin-bottom: 15px;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  margin: 0;
  padding: 15px;
  background-color: #fff;
  box-sizing: border-box;
  border: 1px solid ${props => props.theme.dustGrey};
  border-radius: 8px;
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  font-family: inherit;
  font-size: 1.4rem;
  resize: vertical;

  :focus {
    outline: none;
    border-color: ${props => props.theme.mainColor};
  }

  ::placeholder {
    color: ${props => props.theme.ashesGrey};
  }
`;

const SaveButtonContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

class AttendeeDetails extends React.Component {
  state = {
    hasContactPermission: false,
    notes: '',
    isSaving: false
  };

  isAttendeeScanned = () => {
    const { scannedAttendeesIds, attendeeId } = this.props;
    return scannedAttendeesIds && scannedAttendeesIds.some(
      item => (item.id || item) === attendeeId
    );
  };

  componentDidMount() {
    if (this.props.attendeeId) {
      this.props.fetchAttendee({ attendee_id: this.props.attendeeId });
    }

    if (this.props.canTakeNotes && this.isAttendeeScanned()) {
      this.fetchExistingNotes();
    }

    if (window.cordova && window.cordova.plugins && navigator.contacts) {
      const permissions = window.cordova.plugins.permissions;
      this.checkPermission(permissions.READ_CONTACTS, () => {
        this.checkPermission(permissions.WRITE_CONTACTS, () => {
          this.setState({ hasContactPermission: true });
        });
      });
    }
  }

  convertToArray = (data) => {
    return Array.isArray(data) ? data : [data];
  };

  isNoteValid = (note) => {
    return note?.notes !== undefined && note?.scanned_attendee_id !== undefined;
  };

  fetchExistingNotes = () => {
    const { attendeeId, fetchAttendeeNotes } = this.props;
    
    fetchAttendeeNotes({
      scanned_attendee_id: attendeeId,
      done: (response) => {
        if (!response?.data) return;

        const notes = this.convertToArray(response.data);
        const existingNote = notes[0];

        if (this.isNoteValid(existingNote)) {
          this.setState({ 
            notes: existingNote.notes || '',
            noteId: existingNote.id 
          });
        } else {
          this.setState({ notes: '', noteId: null });
        }
      },
      fail: () => {
        this.setState({ notes: '', noteId: null });
      }
    });
  };

  checkPermission = (permission, cb) => {
    const permissions = window.cordova.plugins.permissions;
    permissions.checkPermission(permission, status => {
      if (status.hasPermission) {
        cb();
      } else {
        permissions.requestPermission(permission, status => {
          if (status.hasPermission) {
            cb();
          }
        });
      }
    });
  };

  componentDidUpdate(prevProps) {
    if (this.props.attendeeId !== prevProps.attendeeId) {
      this.props.fetchAttendee({ attendee_id: this.props.attendeeId });

      if (this.props.canTakeNotes && this.isAttendeeScanned()) {
        this.fetchExistingNotes();
      }
    }
  }

  onClickDownloadVCard = event => {
    const { t, attendee } = this.props;

    if (window.cordova && navigator.contacts) {
      event.stopPropagation();
      event.preventDefault();

      let contact = navigator.contacts.create();
      contact.displayName = attendee.name;
      let name = new window.ContactName();
      name.givenName = attendee.first_name;
      name.familyName = attendee.last_name;
      contact.name = name;

      let emails = [new window.ContactField('work', attendee.email, false)];
      contact.emails = emails;

      if (attendee.phone_number) {
        let phoneNumbers = [new window.ContactField('work', attendee.phone_number, false)];
        contact.phoneNumbers = phoneNumbers;
      }

      let urls = [];
      if (attendee.website_url) {
        urls.push(new window.ContactField('work', attendee.website_url, false));
      }
      if (attendee.linkedin_url) {
        urls.push(new window.ContactField('work', attendee.linkedin_url, false));
      }
      if (urls.length) {
        contact.urls = urls;
      }

      if (attendee.company) {
        let organizations = [
          new window.ContactOrganization(false, 'work', attendee.company, null, attendee.job_title)
        ];
        contact.organizations = organizations;
      }

      contact.save(
        () => {
          alert(t('added_to_contacts'));
        },
        () => { }
      );
    }
  };

  onChangeNotes = event => {
    this.setState({ notes: event.target.value });
  };

  addToFavorites = (attendeeId) => {
    const { matchParams, postFavorite, updateFavModel } = this.props;
    const { appId, eventId } = matchParams;
    
    const favoriteData = {
      app_id: +appId,
      event_id: +eventId,
      favorite_id: attendeeId,
      favorite_type: 'attendee'
    };
    postFavorite(favoriteData);
    
    updateFavModel({
      data: { id: attendeeId, is_favorite: true },
      type: 'attendee'
    });
  };

  onSaveNotes = () => {
    const { attendeeId, postAttendeeNotes, history } = this.props;
    const { notes, noteId } = this.state;

    this.setState({ isSaving: true });

    const payload = {
      scanned_attendee_id: attendeeId,
      notes: notes,
      done: (response) => {
        if (!noteId && response && response.data && response.data.id) {
          this.setState({ noteId: response.data.id });
        }
        
        this.addToFavorites(attendeeId);

        history.goBack();
      },
      always: () => {
        this.setState({ isSaving: false });
      }
    };

    if (noteId) {
      payload.note_id = noteId;
    }

    postAttendeeNotes(payload);
  };

  render() {
    const { t, theme, attendee, matchParams, user, whosHere, canTakeNotes, scannedAttendeesIds } = this.props;

    const isAttendeeScanned = scannedAttendeesIds && scannedAttendeesIds.some(
      item => (item.id || item) === attendee?.id
    );

    const shouldShowNotes = canTakeNotes && isAttendeeScanned;

    return (
      <React.Fragment>
        {attendee ? (
          <CardIcon
            padding={'50px 20px 20px 20px'}
            logoBorderRadius={'50%'}
            image={attendee.avatar_url}
            theme={theme}
            withShadow={true}
          >
            {attendee && attendee.id !== user?.id && whosHere?.is_starable && (
              <FavItem>
                <Star
                  {...{ theme }}
                  size={'40px'}
                  model={attendee}
                  type={'attendee'}
                  matchParams={matchParams}
                />
              </FavItem>
            )}

            <UserContainer>
              <UserName>
                {attendee.first_name} {attendee.last_name}
              </UserName>
              <UserJob>{getJobWithCompany(attendee.job_title, attendee.company)}</UserJob>

              <SocialLinks
                linkedinUrl={attendee.linkedin_url}
                facebookUrl={attendee.facebook_url}
                twitterUrl={attendee.twitter_url}
              />

              <InfoLine icon={IconPoi} label={attendee.country_name} iconColor={theme.ashesGrey} />
              <InfoLine
                icon={IconWebsite}
                label={attendee.website_url}
                iconColor={theme.ashesGrey}
                type="link"
              />

              {attendee.is_shareable &&
                (!window.cordova ||
                  (window.cordova && navigator.contacts && this.state.hasContactPermission)) ? (
                  <DownloadVCard
                    href={attendee.vcard_url}
                    onClick={this.onClickDownloadVCard}
                    download
                  >
                    {t('add_to_contact')}
                  </DownloadVCard>
                ) : null}

              {shouldShowNotes && (
                <NotesSection>
                  <NotesTitle>{t('my_notes')}</NotesTitle>
                  <NotesTextarea
                    value={this.state.notes}
                    onChange={this.onChangeNotes}
                    placeholder={t('write_notes_here')}
                  />
                  <SaveButtonContainer>
                    <Button
                      btntype="primary"
                      onClick={this.onSaveNotes}
                      disabled={this.state.isSaving}
                      width="100%"
                      padding="12px 20px"
                      fontSize="1.4rem"
                    >
                      {this.state.isSaving ? t('btn_saving') : t('save_notes')}
                    </Button>
                  </SaveButtonContainer>
                </NotesSection>
              )}
            </UserContainer>
          </CardIcon>
        ) : null}
      </React.Fragment>
    );
  }
}

export default AttendeeDetails;
